import { InputValidationResult } from "../input";
import { RuleValidationTrigger, IValidationRuleWithBuilder } from "./triggers";
import {
	IsNotNullNorUndefinedRule,
	IsNullOrUndefined,
	IsEqualToOtherRule,
	IsEqualToRule
} from "./object";
import {
	IsNotEmptyRule,
	ConversionRule,
	InlineRuleExecutor,
	InlineRule
} from "./misc";
import {
	HasMaxLengthRule,
	DoesMatchRegExpRule,
	IsUrlRule
} from "./string";
import { IsTrueRule } from "./boolean";
import { HasMinimumEntriesRule, HasMaximumEntriesRule } from "./collection";
import { localize } from "client_legacy/ndaw/culture";
import { IsAfterRule, IsValidDate } from "./date";
import {
	IsPositiveRule,
	HasMaxValueRule,
	HasMinValueRule,
	IsInt32Rule
} from "./number";
import { HasMaxSizeRule, IsSupportedFileType } from "./file";
import { EMAIL_ADDRESS_PATTERN } from "client_legacy/ndaw/stringFormatValidator";
import { ColorContrastRequirements } from "client/Components/ColorPicker/BaseColorPicker";
import { HasContrastRequirementsRule } from "./color";
import { Base64FileUriUploadWithMetadataModel } from "client/Misc/commonTypes";

export interface IValidationContext {
	parentValue: unknown;
}

export interface IValidationRule<TValue> {
	validate: (value: TValue, validationContext: IValidationContext) => Promise<InputValidationResult> | InputValidationResult;

	readonly activationTrigger: RuleValidationTrigger;
	readonly validationTrigger: RuleValidationTrigger;
	readonly fixErrorTrigger: RuleValidationTrigger;
}

export function isNotNullNorUndefined<TValue>(alternativeMessage?: string): IValidationRuleWithBuilder<TValue> {
	return new IsNotNullNorUndefinedRule(alternativeMessage);
}

export function isNullOrUndefined<TValue>(alternativeMessage?: string): IValidationRuleWithBuilder<TValue> {
	return new IsNullOrUndefined<TValue>(alternativeMessage);
}

export function isNotEmpty(alternativeMessage?: string): IValidationRuleWithBuilder<string | nil | ReadonlyArray<unknown>> {
	return new IsNotEmptyRule(alternativeMessage);
}

export function doesNotExceedLength(maxLength: number): IValidationRuleWithBuilder<string | null> {
	return new HasMaxLengthRule(maxLength);
}

export function isEqualToOther<TValue, TParent>(
	otherField: StringKeyOf<TParent>,
	otherFieldName?: string,
	alternativeMessage?: string
): IValidationRuleWithBuilder<TValue> {
	return new IsEqualToOtherRule<TValue, TParent>(otherField, otherFieldName, alternativeMessage);
}

export function isEqualTo<TValue>(value: TValue, alternativeMessage?: string): IValidationRuleWithBuilder<TValue> {
	return new IsEqualToRule<TValue>(value, alternativeMessage);
}

export function isConvertible<TValue>(): IValidationRuleWithBuilder<TValue> {
	return new ConversionRule<TValue>();
}

export function isTrue(alternativeMessage?: string): IValidationRuleWithBuilder<boolean> {
	return new IsTrueRule(alternativeMessage);
}

export function hasMinimumEntries(
	minimumAmount: number,
	alternativeMessage?: string
): IValidationRuleWithBuilder<ReadonlyArray<unknown>> {
	return new HasMinimumEntriesRule(minimumAmount, alternativeMessage);
}

export function hasMaximumEntries(
	maxAmount: number,
	alternativeMessage?: string
): IValidationRuleWithBuilder<ReadonlyArray<unknown>> {
	return new HasMaximumEntriesRule(maxAmount, alternativeMessage);
}

export function isEmpty(alternativeMessage?: string): IValidationRuleWithBuilder<ReadonlyArray<unknown>> {
	return hasMaximumEntries(0, alternativeMessage || localize("_CannotBeSet"));
}

export function isAfter(minimalDateTime: Date, alternativeMessage?: string): IValidationRuleWithBuilder<Date | null> {
	return new IsAfterRule(minimalDateTime, alternativeMessage);
}

export function isValidDate(): IValidationRuleWithBuilder<Date | null> {
	return new IsValidDate();
}

export function isPositive(alternativeMessage?: string): IValidationRuleWithBuilder<number | null> {
	return new IsPositiveRule(alternativeMessage);
}

export function isNotLessThan(minValue: number): IValidationRuleWithBuilder<number | null> {
	return new HasMinValueRule(minValue);
}

export function doesNotExceed(maxValue: number): IValidationRuleWithBuilder<number | null> {
	return new HasMaxValueRule(maxValue);
}

export function isInt32(): IValidationRuleWithBuilder<number | null> {
	return new IsInt32Rule();
}

export function inline<TValue>(rule: InlineRuleExecutor<TValue>): IValidationRuleWithBuilder<TValue> {
	return new InlineRule(rule);
}

export function doesMatchRegExp(regexp: RegExp, errorMessage: string): IValidationRuleWithBuilder<string | null> {
	return new DoesMatchRegExpRule(regexp, errorMessage);
}

export function isEmailAddress(alternativeMessage?: string): IValidationRuleWithBuilder<string | null> {
	return new DoesMatchRegExpRule(
		EMAIL_ADDRESS_PATTERN,
		alternativeMessage || localize("_NotAvalidEmailAddressFormat")
	);
}

export type IsUrlOptions = {
	allowHttpsOnly: boolean;
	allowQueryString?: boolean;
	alternativeMessage?: string;
	alternativeMessageForNonHttps?: string;
};

export function isUrl(
	options: IsUrlOptions
): IValidationRuleWithBuilder<string | null> {
	const {
		allowHttpsOnly,
		allowQueryString = true,
		alternativeMessage,
		alternativeMessageForNonHttps
	} = options;

	return new IsUrlRule(
		allowHttpsOnly,
		allowQueryString,
		alternativeMessage,
		alternativeMessageForNonHttps
	);
}

export function hasMaxSize(
	maxSize: number,
	alternativeMessage?: string
): IValidationRuleWithBuilder<OptionalModel<Base64FileUriUploadWithMetadataModel | null>> {
	return new HasMaxSizeRule(maxSize, alternativeMessage);
}

export function isSupportedFileType(
	supportedFileTypes: readonly string[],
	alternativeMessage?: string
): IValidationRuleWithBuilder<OptionalModel<Base64FileUriUploadWithMetadataModel | null>> {
	return new IsSupportedFileType(supportedFileTypes, alternativeMessage);
}

export function hasMinimumContrastRatio(
	contrastRequirements: ColorContrastRequirements,
	alternativeMessage?: string
): IValidationRuleWithBuilder<string | null> {
	return new HasContrastRequirementsRule(contrastRequirements, alternativeMessage);
}
